import {BreadcrumbType, DataStatus} from 'platform/components';

import {
  useListInterestAssignmentConditionsQuery,
  useUpdateInterestAssignmentConditionMutation,
} from '@dms/api/core';
import {i18n} from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate, useRequiredParams} from 'shared/web';

import {AssignmentConditionForm} from '../../components/AssignmentConditionForm/AssignmentConditionForm';
import {
  AssignmentConditionFormValues,
  formValuesToRequestBody,
} from '../../components/AssignmentConditionForm/schemas/assignmentConditionFormSchema';

const breadcrumbs: BreadcrumbType[] = [
  {
    label: i18n.t('entity.interest.labels.interest'),
    href: settingsRoutes.interest,
  },
  {
    label: i18n.t('entity.interest.labels.SalesLeadsAssignment'),
    href: settingsRoutes.salesLeadsAssignment,
  },
];

export function SalesLeadsAssignmentEdit() {
  const params = useRequiredParams<{id: string}>();
  const navigate = useNavigate();

  const {
    data: conditions,
    isLoading,
    isError,
  } = useListInterestAssignmentConditionsQuery({
    teamId: null,
  });
  const [updateCondition] = useUpdateInterestAssignmentConditionMutation();

  const handleSubmit = (
    values: AssignmentConditionFormValues,
    blockerContext?: {wasSubmittedFromBlocker?: boolean; reportApiFailure?: VoidFunction}
  ): Promise<void> =>
    updateCondition({
      conditionId: params.id,
      updateInterestAssignmentConditionRequestBody: formValuesToRequestBody(values),
    })
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.salesLeadsAssignment);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  const handleDiscard = (navigateWithoutBlocker: (path: string) => void) =>
    navigateWithoutBlocker(settingsRoutes.salesLeadsAssignment);

  const condition = conditions?.find((c) => c.id === params.id);

  return (
    <SettingsTemplate
      header={{
        breadcrumbs,
        title: condition?.name ?? i18n.t('entity.interest.labels.editCondition'),
      }}
      headerVariant="v2"
      data-testid={testIds.settings.salesLeadsAssignmentEdit('page')}
      isLoading={isLoading}
      isError={isError}
    >
      <DataStatus isEmpty={!isLoading && !isError && !condition}>
        <AssignmentConditionForm
          baseTestId={testIds.settings.salesLeadsAssignmentEdit}
          initialData={condition}
          onSubmit={handleSubmit}
          onDiscard={handleDiscard}
        />
      </DataStatus>
    </SettingsTemplate>
  );
}
